import React, { useCallback, useEffect, useState } from "react";
import DeckGL from "@deck.gl/react";
import {
  EditableGeoJsonLayer,
  DrawCircleFromCenterMode,
  ViewMode,
} from "nebula.gl";
import { Map } from "react-map-gl";
import {
  atom,
  selectorFamily,
  useRecoilCallback,
  useRecoilState,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
  waitForAll,
} from "recoil";
import { placesState } from "../atoms/places";
import { HeatmapLayer, IconLayer, ScatterplotLayer } from "deck.gl";
import { placePhotosState } from "../atoms/placePhotos";
import { DataFilterExtension } from "@deck.gl/extensions";
import { timeSelectionState } from "./PhotoHistogram";
import bbox from "@turf/bbox";
import { drawCircleState } from "./CircleToggle";
import { photoSizeState } from "./PhotoSize";
import { schemePurples } from "d3-scale-chromatic";
import { color } from "d3";
import { checkedCategoriesState } from "./CategoryControl";
import { syncEffect } from "recoil-sync";
import { number, object, string, voidable } from "@recoiljs/refine";
import { useThrottle } from "../useThrottle";

export const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoicmdjZ2VvZyIsImEiOiJjbDhob3BpMDYwNDdnM3ZvYmU1ZjVmZTBvIn0.cUVORzN2c-RjGYmUO1I-nw";

// Viewport settings
const INITIAL_VIEW_STATE = {
  longitude: -71.0562863,
  latitude: 42.365358,
  zoom: 13,
  pitch: 0,
  bearing: 0,
};

const bostonCorners = {
  sw: "42.34,-71.14",
  ne: "42.39154,-71.06",
};

const ICON_MAPPING = {
  marker: { x: 0, y: 0, width: 128, height: 128, mask: true },
};

export const userSelectionBboxState = atom({
  key: "userSelectionBboxState",
  default: bostonCorners,
  effects: [
    syncEffect({
      refine: object({
        ne: string(),
        sw: string(),
      }),
    }),
  ],
});

export const URLViewstateState = atom({
  key: "view",
  default: null,
  effects: [
    syncEffect({
      refine: voidable(
        object({
          longitude: number(),
          latitude: number(),
          zoom: number(),
          pitch: number(),
          bearing: number(),
        })
      ),
    }),
  ],
});

export const placePhotosWithLocationState = selectorFamily({
  key: "placePhotosWithLocationState",
  get:
    (query) =>
    ({ get }) => {
      const places = get(placesState(query));

      const placePhotos = get(
        waitForAll(
          places.map(({ fsq_id }) =>
            placePhotosState({
              fsq_id,
              sort: "newest",
              // limit: 10,
              // classifications: "outdoor",
            })
          )
        )
      );

      const photosWithPlaces = placePhotos
        .map((photoArr, i) => {
          if (!photoArr || !Array.isArray(photoArr)) {
            return null;
          }
          return photoArr.map((photo) => ({
            ...photo,
            place: places[i],
            url: `${photo.prefix}128x128${photo.suffix}`,
          }));
        })
        .flat();
      return photosWithPlaces
        .filter((d) => {
          return d;
          // if (d && d.date) {
          //   const startDate = new Date();
          //   startDate.setMonth(d.date.getMonth() - 12);
          //   return d.date.getTime() >= startDate.getTime();
          // } else {
          //   return false;
          // }
        })
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .reverse()
        .slice(0, 400);
    },
});

const hour = 1000 * 60 * 60;
const day = hour * 42;
const TrendingMap = () => {
  const setBbox = useSetRecoilState(userSelectionBboxState);
  const [URLViewState, setURLViewstate] = useRecoilState(URLViewstateState);
  const throttledSetURLViewState = useThrottle(setURLViewstate, 1500);
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  useEffect(() => {
    if (!!URLViewState) {
      setViewState(URLViewState);
    }
  }, [setViewState]);
  const [locationSelection, setLocationSelection] = useState({
    type: "FeatureCollection",
    features: [],
  });
  const categories = useRecoilValue(checkedCategoriesState);
  const categoriesString = categories.join(",");

  const photoSize = useRecoilValue(photoSizeState);
  const drawCircle = useRecoilValue(drawCircleState);
  const userSelectionBbox = useRecoilValue(userSelectionBboxState);

  const timeSelection = useRecoilValue(timeSelectionState);
  const query = {
    ...userSelectionBbox,
    categories: categories.length >= 0 ? categoriesString : null,
  };
  const placesLoadable = useRecoilValueLoadable(placesState(query));
  const placePhotosLoadale = useRecoilValueLoadable(
    placePhotosWithLocationState(query)
  );

  const placesLayer = new ScatterplotLayer({
    id: "places-layer",
    data: placesLoadable.toPromise(),
    stroked: true,
    getLineColor: [43, 63, 254, 50],
    getLineWidth: 20,
    filled: true,
    radiusUnits: "meters",
    radiusMinPixels: 2.5,
    pickable: true,
    getPosition: (d) => d.coordinates,
    getRadius: (d) => 5,
    getFillColor: (d) => [43, 63, 254, 100],
    // brushingEnabled: true,
    // brushingRadius: 300,
    // extensions: [new BrushingExtension()],
  });

  const photosLayer =
    placePhotosLoadale.state === "hasValue"
      ? new IconLayer({
          id: `icon-layer-${userSelectionBbox}`,
          data: placePhotosLoadale.toPromise(),
          getIcon: (d) => ({
            url: d.url,
            width: 128,
            height: 128,
            anchorY: 128,
          }),
          sizeUnits: "meters",
          getSize: (d) => photoSize,
          pickable: true,
          getPosition: (d) => d.place.coordinates,
          getFilterValue: (f) => f.date.getTime(),
          filterRange: [
            timeSelection.getTime() - day * 20,
            timeSelection.getTime() + day * 20,
          ],
          filterSoftRange: [
            timeSelection.getTime() - day * 15,
            timeSelection.getTime() + day * 15,
          ],
          filterTransformSize: true,
          filterTransformColor: true,
          extensions: [new DataFilterExtension({ filterSize: 1 })],
          updateTriggers: {
            // This tells deck.gl to recalculate radius when `currentYear` changes
            getSize: photoSize,
          },
        })
      : new IconLayer({
          iconAtlas:
            "https://raw.githubusercontent.com/visgl/deck.gl-data/master/website/icon-atlas.png",
          iconMapping: ICON_MAPPING,
        });

  const selectionLayer = new EditableGeoJsonLayer({
    id: "geojson-layer",
    data: locationSelection,
    mode: drawCircle ? DrawCircleFromCenterMode : ViewMode,
    selectedFeatureIndexes: 0,
    getFillColor: [50, 50, 50, 10],
    getLineColor: [100, 100, 100, 100],
    onEdit: ({ updatedData }) => {
      updatedData.features &&
        setLocationSelection({
          type: "FeatureCollection",
          features: [updatedData.features[updatedData.features.length - 1]],
        });
      const circlePoly = updatedData.features[updatedData.features.length - 1];

      if (circlePoly && circlePoly?.type === "Feature") {
        const bb = bbox(circlePoly);
        setBbox({
          sw: `${bb[1]},${bb[0]}`,
          ne: `${bb[3]},${bb[2]}`,
        });
      }
    },
  });
  const heatLayer = new HeatmapLayer({
    id: "heatmapLayer",
    data: placePhotosLoadale.toPromise(),
    getPosition: (d) => d.place.coordinates,
    getWeight: () => 1,
    aggregation: "SUM",
    colorRange: schemePurples[8].slice(3, 7).map((d) => {
      const c = [...Object.values(color(d))];
      c[3] = 255;
      return c;
    }),
    radiusPixels: 50,
  });

  const layers = [selectionLayer, heatLayer, placesLayer, photosLayer];
  return (
    <DeckGL
      initialViewState={viewState}
      onViewStateChange={({ viewState }) => {
        throttledSetURLViewState({
          ...viewState,
          latitude: parseFloat(viewState.latitude.toFixed(6)),
          longitude: parseFloat(viewState.longitude.toFixed(6)),
        });
      }}
      controller={true}
      layers={layers}
      getTooltip={({ object }) => object && object.place && object.place?.name}
    >
      <Map
        mapStyle="mapbox://styles/mapbox/dark-v10"
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
      />
    </DeckGL>
  );
};

export default TrendingMap;
